import React, { useState, useEffect, useRef, useMemo, memo } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { profileApi, DashboardStats, UnpaidRentRoomItem } from '../../../services/profileApi';
import { contractApi } from '../../../services/contractApi';
import { propertyApi } from '../../../services/propertyApi';
import BusinessDashboard from './dashboards/BusinessDashboard';
import { ProfileType } from '../../../utils/profileType';
import { DASHBOARD_REFRESH_EVENT } from '../../../utils/dashboardRefresh';
import { formatPaymentTerm, formatRentMonthLabel, getPropertyAddress } from '../../../utils/rentalPaymentHistory';
import { isNativeMobileApp } from '../../../utils/mobileApp';

interface DashboardModalProps {
  profileType: ProfileType;
  mode?: 'modal' | 'inline';
}

interface PendingRoomItem {
  id: number;
  address: string;
  roomNumber: string | null;
}

interface DashboardData {
  totalProperties: number;
  activeProperties: number;
  totalRevenue: number;
  pendingActions: number;
  recentActivities: any[];
  chartData: {
    labels: string[];
    datasets: any[];
  };
}

const DashboardModal: React.FC<DashboardModalProps> = ({ profileType, mode = 'modal' }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isInline = mode === 'inline';
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPeriod] = useState('month');
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [propertyCounts, setPropertyCounts] = useState({ total: 0, rented: 0, pending: 0, unpaidRooms: 0 });
  const [unpaidRentItems, setUnpaidRentItems] = useState<UnpaidRentRoomItem[]>([]);
  const [pendingRoomItems, setPendingRoomItems] = useState<PendingRoomItem[]>([]);
  const [currentMonthRentReceived, setCurrentMonthRentReceived] = useState(0);
  const [currentMonthLabel, setCurrentMonthLabel] = useState(() => {
    const now = new Date();
    return formatRentMonthLabel(now.getFullYear(), now.getMonth() + 1);
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadInFlightRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);
  const lastFetchedAtRef = useRef(0);
  const isNative = isNativeMobileApp();
  const listLimit = isNative ? 50 : 200;
  const cacheMs = isNative ? 90_000 : 20_000;
  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({
    profileSpecific: true,
    benchmark: false,
    activities: true
  });

  const openModal = () => setIsOpen(true);
  const closeModal = () => setIsOpen(false);

  // Load dashboard data from API
  useEffect(() => {
    if ((isInline || isOpen) && user?.id) {
      void loadDashboardData();
    }
    // Chỉ phụ thuộc user.id để tránh load lại khi object user đổi reference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInline, isOpen, user?.id, selectedPeriod, profileType]);

  useEffect(() => {
    const handleRefresh = () => {
      if ((isInline || isOpen) && user?.id) {
        void loadDashboardData(true);
      }
    };

    window.addEventListener(DASHBOARD_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(DASHBOARD_REFRESH_EVENT, handleRefresh);
  }, [isInline, isOpen, user?.id, selectedPeriod, profileType]);

  useEffect(() => {
    if (isNative) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && (isInline || isOpen) && user?.id) {
        void loadDashboardData(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isInline, isOpen, isNative, user?.id, selectedPeriod, profileType]);



  const loadPropertyCounts = async () => {
    if (!user?.id) {
      setPropertyCounts({ total: 0, rented: 0, pending: 0, unpaidRooms: 0 });
      setUnpaidRentItems([]);
      setPendingRoomItems([]);
      return;
    }

    const normalizePropertyStatus = (status: unknown) => {
      const value = String(status || '').trim().toLowerCase();
      if (['rented', 'occupied', 'completed', 'closed', 'leased', 'booked'].includes(value)) {
        return 'rented';
      }
      return 'pending';
    };

    try {
      const [propertiesResult, contractsResult, unpaidRentResult] = await Promise.allSettled([
        propertyApi.getProperties({ owner_id: Number(user.id), limit: listLimit }),
        contractApi.getContracts({
          limit: listLimit,
        }),
        profileType === 'landlord' || profileType === 'business'
          ? profileApi.getUnpaidRentRooms()
          : Promise.resolve(null),
      ]);

      const properties = Array.isArray(propertiesResult.status === 'fulfilled' ? propertiesResult.value.data : [])
        ? (propertiesResult.status === 'fulfilled' ? propertiesResult.value.data : [])
        : [];
      const contracts = Array.isArray(contractsResult.status === 'fulfilled' ? contractsResult.value : [])
        ? (contractsResult.status === 'fulfilled' ? contractsResult.value : [])
        : [];

      const rentedPropertyIds = new Set<number>();
      contracts.forEach((contract: any) => {
        const contractStatus = String(contract?.status || '').trim().toLowerCase();
        const propertyId = Number(contract?.property_id);
        const isRentedContract = ['active', 'completed', 'signed', 'confirmed', 'approved'].includes(contractStatus);
        if (Number.isFinite(propertyId) && propertyId > 0 && isRentedContract) {
          rentedPropertyIds.add(propertyId);
        }
      });

      const total = properties.length;
      let rented = 0;

      const pendingRooms: PendingRoomItem[] = [];
      properties.forEach((property: any) => {
        const propertyId = Number(property?.id);
        const normalizedStatus = normalizePropertyStatus(property?.status);
        const hasActiveContract = Number.isFinite(propertyId) && rentedPropertyIds.has(propertyId);

        if (hasActiveContract || normalizedStatus === 'rented') {
          rented += 1;
          return;
        }

        pendingRooms.push({
          id: Number.isFinite(propertyId) ? propertyId : pendingRooms.length,
          address: getPropertyAddress(property),
          roomNumber: String(property?.room_number || '').trim() || null,
        });
      });

      const pending = Math.max(total - rented, 0);

      let unpaidRooms = 0;
      let unpaidItems: UnpaidRentRoomItem[] = [];
      if (unpaidRentResult.status === 'fulfilled' && unpaidRentResult.value) {
        unpaidRooms = unpaidRentResult.value.count ?? 0;
        unpaidItems = unpaidRentResult.value.items ?? [];
      }

      setPropertyCounts({ total, rented, pending, unpaidRooms });
      setUnpaidRentItems(unpaidItems);
      setPendingRoomItems(pendingRooms);
    } catch (err) {
      console.error('Error loading property counts:', err);
      setPropertyCounts({ total: 0, rented: 0, pending: 0, unpaidRooms: 0 });
      setUnpaidRentItems([]);
      setPendingRoomItems([]);
    }
  };

  const loadDashboardData = async (force = false) => {
    if (loadInFlightRef.current) return;
    if (!force && hasLoadedOnceRef.current && Date.now() - lastFetchedAtRef.current < cacheMs) {
      return;
    }

    loadInFlightRef.current = true;
    if (!hasLoadedOnceRef.current) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const [apiStats] = await Promise.all([
        profileApi.getDashboardStats('month', profileType) as Promise<DashboardStats>,
        loadPropertyCounts(),
      ]);

      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      setCurrentMonthLabel(formatRentMonthLabel(year, month));

      const monthTotal = Number(apiStats.contract_analytics?.total_received_current_month) || 0;
      const yearTotal = Number(apiStats.contract_analytics?.total_received_current_year) || 0;
      setCurrentMonthRentReceived(monthTotal);
      setStats(apiStats);
      const mergedStats = apiStats;

      let chartLabels: string[] = [];
      if (selectedPeriod === 'week') {
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now);
          date.setDate(date.getDate() - i * 7);
          const weekNum = Math.ceil(date.getDate() / 7);
          chartLabels.push(`W${weekNum}`);
        }
      } else if (selectedPeriod === 'quarter') {
        chartLabels = ['Q1', 'Q2', 'Q3', 'Q4'];
      } else if (selectedPeriod === 'year') {
        chartLabels = ['Y1', 'Y2', 'Y3', 'Y4'];
      } else {
        for (let i = 11; i >= 0; i--) {
          const date = new Date(now);
          date.setMonth(date.getMonth() - i);
          chartLabels.push(`T${date.getMonth() + 1}`);
        }
      }

      const datasets: any[] = [];
      if (profileType === 'tenant' && mergedStats.monthly_revenue && mergedStats.monthly_revenue.length > 0) {
        datasets.push({
          label: 'Chi phí thuê',
          data: mergedStats.monthly_revenue,
          borderColor: '#C4A35A',
          backgroundColor: 'rgba(0, 100, 0, 0.1)',
          tension: 0.4
        });
        if (mergedStats.monthly_payments && mergedStats.monthly_payments.length > 0) {
          datasets.push({
            label: 'Hóa đơn',
            data: mergedStats.monthly_payments,
            borderColor: '#8B0000',
            backgroundColor: 'rgba(139, 0, 0, 0.1)',
            tension: 0.4
          });
        }
      } else if (mergedStats.monthly_revenue && mergedStats.monthly_revenue.length > 0) {
        datasets.push({
          label: getChartLabel(),
          data: mergedStats.monthly_revenue,
          borderColor: '#C4A35A',
          backgroundColor: 'rgba(0, 100, 0, 0.1)',
          tension: 0.4
        });
      }

      setDashboardData({
        totalProperties: mergedStats.total_properties || 0,
        activeProperties: mergedStats.active_properties || 0,
        totalRevenue: profileType === 'tenant'
          ? (mergedStats.profile_specific_stats?.total_rental_cost || 0)
          : yearTotal || mergedStats.total_revenue || 0,
        pendingActions: mergedStats.pending_actions || 0,
        recentActivities: [],
        chartData: {
          labels: chartLabels,
          datasets: datasets.length > 0 ? datasets : [{
            label: getChartLabel(),
            data: generateChartData(mergedStats),
            borderColor: '#C4A35A',
            backgroundColor: 'rgba(0, 100, 0, 0.1)',
            tension: 0.4
          }]
        }
      });
      hasLoadedOnceRef.current = true;
      lastFetchedAtRef.current = Date.now();
    } catch (err: any) {
      if (!isNative) {
        await loadPropertyCounts();
      }
      console.error('Error loading dashboard data:', err);
      setError(null);
      setDashboardData({
        totalProperties: 0,
        activeProperties: 0,
        totalRevenue: 0,
        pendingActions: 0,
        recentActivities: [],
        chartData: {
          labels: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'],
          datasets: []
        }
      });
      setStats({
        total_properties: 0,
        active_properties: 0,
        total_revenue: 0,
        total_spent: 0,
        total_commission: 0,
        total_views: 0,
        total_favorites: 0,
        response_rate: 0,
        customer_rating: 0,
        total_reviews: 0,
        pending_actions: 0,
        profile_specific_stats: {
          total_tenants: 0,
          occupancy_rate: 0,
          average_rental_duration_days: 0,
          total_deposit: 0
        }
      } as DashboardStats);
    } finally {
      loadInFlightRef.current = false;
      setIsLoading(false);
    }
  };

  const getChartLabel = () => {
    switch (profileType) {
      case 'tenant': return 'Chi phí thuê';
      case 'landlord': return 'Doanh thu';
      case 'business': return 'Doanh thu';
      default: return 'Thống kê';
    }
  };

  const getEmptyDashboardData = (): DashboardData => ({
    totalProperties: 0,
    activeProperties: 0,
    totalRevenue: 0,
    pendingActions: 0,
    recentActivities: [],
    chartData: {
      labels: ['T1', 'T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'T8', 'T9', 'T10', 'T11', 'T12'],
      datasets: [],
    },
  });

  const data = dashboardData || getEmptyDashboardData();

  const unpaidRoomGroups = useMemo(() => {
    const groups = new Map<number, {
      propertyId: number;
      address: string;
      roomNumber: string | null;
      periods: { key: string; term: string }[];
    }>();

    unpaidRentItems.forEach((item) => {
      const existing = groups.get(item.property_id);
      const address = (item.address || item.property_title || item.room_label || '').trim();
      const roomNumber = (item.room_number || '').trim() || null;
      const term = item.payment_term
        || formatPaymentTerm(item.period_start, item.period_end)
        || item.period_label
        || '';

      if (!existing) {
        groups.set(item.property_id, {
          propertyId: item.property_id,
          address,
          roomNumber,
          periods: term ? [{ key: item.period_start, term }] : [],
        });
        return;
      }

      if (term && !existing.periods.some((period) => period.key === item.period_start)) {
        existing.periods.push({ key: item.period_start, term });
      }
    });

    return Array.from(groups.values()).map((group) => ({
      ...group,
      periods: [...group.periods].sort((a, b) => a.key.localeCompare(b.key)),
    }));
  }, [unpaidRentItems]);

  const generateChartData = (stats: DashboardStats) => {
    // Use actual monthly revenue data from API if available
    if (stats.monthly_revenue && stats.monthly_revenue.length > 0) {
      return stats.monthly_revenue.map(val => Math.floor(val || 0));
    }
    // If no monthly data, use zeros instead of mock data
    return Array.from({ length: 12 }, () => 0);
  };

  const comparisonMetrics = useMemo(() => {
    if (!stats || !stats.monthly_revenue || stats.monthly_revenue.length === 0) {
      return { previous: 0, current: 0, change: 0, average: 0 };
    }

    const monthly = stats.monthly_revenue;
    const current = monthly[monthly.length - 1] || 0;
    const previous = monthly[monthly.length - 2] || 0;
    const change = previous > 0 ? ((current - previous) / previous) * 100 : (current > 0 ? 100 : 0);
    const average = monthly.length > 0 ? monthly.reduce((a, b) => a + b, 0) / monthly.length : 0;

    return { previous, current, change, average };
  }, [stats]);

  const CHART_VIEW_WIDTH = 960;
  const CHART_VIEW_HEIGHT = 420;
  const CHART_PADDING = 48;

  const chartSvgProps = {
    viewBox: `0 0 ${CHART_VIEW_WIDTH} ${CHART_VIEW_HEIGHT}`,
    preserveAspectRatio: 'xMidYMid meet' as const,
    className: 'block h-full w-full',
    role: 'img' as const,
  };

  // Generate SVG line chart - Simple and minimal
  const renderAreaChart = () => {
    if (!data?.chartData?.datasets || data.chartData.datasets.length === 0) {
      return null;
    }

    const width = CHART_VIEW_WIDTH;
    const height = CHART_VIEW_HEIGHT;
    const padding = CHART_PADDING;
    
    // Get all data from all datasets to calculate max value
    const allData: number[] = [];
    data.chartData.datasets.forEach((ds: any) => {
      if (ds.data) allData.push(...ds.data);
    });
    const maxValue = Math.max(...allData, 1);
    
    const pointSpacing = (width - padding * 2) / ((data.chartData.labels.length - 1) || 1);
    
    // Color schemes for multi-line
    const colors = [
      { line: '#C4A35A', area: 'rgba(0, 100, 0, 0.1)' },    // Green - Chi phí thuê
      { line: '#8B0000', area: 'rgba(139, 0, 0, 0.1)' }     // Dark red - Hóa đơn
    ];

    // Generate smooth bezier curve path
    const generateSmoothPath = (pts: { x: number; y: number; value: number }[]) => {
      if (pts.length < 2) return '';
      let path = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) {
        const curr = pts[i];
        const prev = pts[i - 1];
        const controlX = (prev.x + curr.x) / 2;
        const controlY = (prev.y + curr.y) / 2;
        path += ` Q ${controlX} ${controlY} ${curr.x} ${curr.y}`;
      }
      return path;
    };

    return (
      <svg {...chartSvgProps}>
        {/* Horizontal grid lines */}
        {[0, 25, 50, 75, 100].map((pct) => (
          <line
            key={`grid-${pct}`}
            x1={padding}
            y1={height - padding - (pct / 100) * (height - padding * 2)}
            x2={width - padding}
            y2={height - padding - (pct / 100) * (height - padding * 2)}
            stroke="rgba(0,0,0,0.02)"
            strokeWidth="1"
          />
        ))}

        {/* Lines for each dataset */}
        {data.chartData.datasets.map((dataset: any, dsIdx: number) => {
          if (!dataset.data) return null;
          
          const color = colors[dsIdx % colors.length];
          const points: { x: number; y: number; value: number }[] = [];
          
          dataset.data.forEach((value: number, idx: number) => {
            const x = padding + idx * pointSpacing;
            const y = height - padding - (value / maxValue) * (height - padding * 2);
            points.push({ x, y, value });
          });

          const smoothPath = generateSmoothPath(points);
          const fillPath = `${smoothPath} L ${width - padding} ${height - padding} L ${padding} ${height - padding} Z`;

          return (
            <g key={`dataset-${dsIdx}`}>
              {/* Fill */}
              <path d={fillPath} fill={color.area} stroke="none" opacity="0.3" />
              {/* Line */}
              <path d={smoothPath} fill="none" stroke={color.line} strokeWidth="2" vectorEffect="non-scaling-stroke" strokeLinecap="round" strokeLinejoin="round" />
              {/* Points */}
              {points.map((p, idx) => (
                <circle key={`point-${dsIdx}-${idx}`} cx={p.x} cy={p.y} r="2.5" fill={color.line} opacity="0.7" />
              ))}
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.chartData.labels.map((label: string, idx: number) => {
          const x = padding + (idx / (data.chartData.labels.length - 1 || 1)) * (width - padding * 2);
          return (
            <text key={`label-${idx}`} x={x} y={height - 10} textAnchor="middle" className="text-xs" fill="rgba(0,0,0,0.4)">
              {label}
            </text>
          );
        })}

        {/* Legend */}
        {data.chartData.datasets.length > 1 && (
          data.chartData.datasets.map((dataset: any, idx: number) => {
            const color = colors[idx % colors.length];
            return (
              <g key={`legend-${idx}`}>
                <line x1={20} y1={height - 60 + idx * 20} x2={40} y2={height - 60 + idx * 20} stroke={color.line} strokeWidth="2" />
                <text x={50} y={height - 55 + idx * 20} className="text-xs" fill="rgba(0,0,0,0.6)">
                  {dataset.label}
                </text>
              </g>
            );
          })
        )}
      </svg>
    );
  };

  const renderLineChart = () => {
    if (!data?.chartData?.datasets || data.chartData.datasets.length === 0) {
      return null;
    }

    const width = CHART_VIEW_WIDTH;
    const height = CHART_VIEW_HEIGHT;
    const padding = CHART_PADDING;
    
    // Get all data from all datasets to calculate max value
    const allData: number[] = [];
    data.chartData.datasets.forEach((ds: any) => {
      if (ds.data) allData.push(...ds.data);
    });
    const maxValue = Math.max(...allData, 1);
    
    const pointSpacing = (width - padding * 2) / ((data.chartData.labels.length - 1) || 1);
    
    // Color schemes for multi-line
    const colors = [
      { line: '#C4A35A' },    // Green - Chi phí thuê
      { line: '#8B0000' }     // Dark red - Hóa đơn
    ];

    return (
      <svg {...chartSvgProps}>
        {/* Horizontal grid lines */}
        {[0, 25, 50, 75, 100].map((pct) => (
          <line
            key={`grid-${pct}`}
            x1={padding}
            y1={height - padding - (pct / 100) * (height - padding * 2)}
            x2={width - padding}
            y2={height - padding - (pct / 100) * (height - padding * 2)}
            stroke="rgba(0,0,0,0.04)"
            strokeWidth="1"
          />
        ))}

        {/* Lines for each dataset */}
        {data.chartData.datasets.map((dataset: any, dsIdx: number) => {
          if (!dataset.data) return null;
          
          const color = colors[dsIdx % colors.length];
          const points: { x: number; y: number; value: number }[] = [];
          
          dataset.data.forEach((value: number, idx: number) => {
            const x = padding + idx * pointSpacing;
            const y = height - padding - (value / maxValue) * (height - padding * 2);
            points.push({ x, y, value });
          });

          const linePath = points.map((p, idx) => `${idx === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');

          return (
            <g key={`dataset-${dsIdx}`}>
              {/* Line */}
              <path d={linePath} fill="none" stroke={color.line} strokeWidth="2" vectorEffect="non-scaling-stroke" />
              {/* Points */}
              {points.map((p, idx) => (
                <circle key={`point-${dsIdx}-${idx}`} cx={p.x} cy={p.y} r="3" fill={color.line} stroke="white" strokeWidth="1" />
              ))}
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.chartData.labels.map((label: string, idx: number) => {
          const x = padding + (idx / (data.chartData.labels.length - 1 || 1)) * (width - padding * 2);
          return (
            <text key={`label-${idx}`} x={x} y={height - 10} textAnchor="middle" className="text-xs" fill="rgba(0,0,0,0.5)">
              {label}
            </text>
          );
        })}

        {/* Legend */}
        {data.chartData.datasets.length > 1 && (
          data.chartData.datasets.map((dataset: any, idx: number) => {
            const color = colors[idx % colors.length];
            return (
              <g key={`legend-${idx}`}>
                <line x1={20} y1={height - 60 + idx * 20} x2={40} y2={height - 60 + idx * 20} stroke={color.line} strokeWidth="2" />
                <text x={50} y={height - 55 + idx * 20} className="text-xs" fill="rgba(0,0,0,0.6)">
                  {dataset.label}
                </text>
              </g>
            );
          })
        )}
      </svg>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const getProfileTitle = () => {
    switch (profileType) {
      case 'tenant':
        return 'Tổng quan khách thuê';
      case 'landlord':
        return 'Tổng quan chủ nhà';
      case 'business':
        return 'Tổng quan doanh nghiệp';
      default:
        return 'Tổng quan';
    }
  };

  if (!isInline && !isOpen) {
    return (
      <button
        onClick={openModal}
        title={t('sidebarButtons.dashboard')}
        className="w-12 h-12 rounded-xl bg-transparent text-surface-black/70 cursor-pointer transition-all duration-200 flex items-center justify-center hover:bg-primary-light hover:text-primary hover:shadow-md relative group"
      >
        <i className="fas fa-chart-pie text-base text-black group-hover:scale-110 transition-transform duration-200"></i>
      </button>
    );
  }

  const trendChart = isNative && isInline
    ? null
    : (renderAreaChart() ?? renderLineChart());

  const modalContent = (
    <div className={isInline ? 'w-full' : 'fixed inset-0 bg-black/20 z-[9999] flex items-center justify-center backdrop-blur-sm p-2 sm:p-0'}>
      <div className={isInline
        ? 'bg-white w-full rounded-xl shadow-sm border border-black/5 overflow-hidden'
        : 'bg-white w-full max-w-full sm:max-w-6xl mx-auto max-h-[90vh] overflow-hidden min-w-[320px] rounded-lg'}>
        {/* Modal Header - Minimalist */}
        {!(isNative && isInline) && (
        <div className="border-b border-black/10 p-3 sm:p-6 flex items-center justify-between rounded-t-lg">
          <h2 className="text-base sm:text-xl font-medium text-black line-clamp-1">{getProfileTitle()}</h2>
          {!isInline && (
            <button onClick={closeModal} className="w-8 h-8 flex items-center justify-center text-black/60 hover:text-black transition-colors rounded-full hover:bg-black/5 flex-shrink-0">
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>
        )}

        {/* Modal Content - Simple Stats Only */}
        <div
          className={isInline ? 'p-3 sm:p-8' : 'overflow-y-auto p-3 sm:p-8'}
          style={isInline ? undefined : { maxHeight: 'calc(90vh - 70px)' }}
        >

          {/* Loading State */}
          {isLoading && !dashboardData && (
            <div className="mb-8 flex items-center justify-center py-12">
              <div className="text-center">
                <i className="fas fa-spinner fa-spin text-3xl text-black/40 mb-4"></i>
                <p className="text-sm text-black/60">Đang tải dữ liệu...</p>
              </div>
            </div>
          )}

          {/* Error State - Minimalist */}
          {error && !isLoading && (
            <div className="mb-8 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center gap-2 text-sm text-red-600">
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
                <button
                  onClick={() => void loadDashboardData(true)}
                  className="ml-auto text-xs underline hover:no-underline"
                >
                  Thử lại
                </button>
              </div>
            </div>
          )}

          {profileType === 'business' && stats && (
            <BusinessDashboard
              stats={stats}
              expandedSections={expandedSections}
              setExpandedSections={setExpandedSections}
              currentRevenue={comparisonMetrics.current}
              revenueChangePercent={comparisonMetrics.change}
            />
          )}

          <div className="space-y-6">
            <div className={`grid items-start gap-3 sm:gap-4 ${isNative ? 'grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 xl:grid-cols-3'}`}>
              <div className={isNative ? 'col-span-2 grid grid-cols-2 items-stretch gap-3' : 'contents'}>
                <div className="flex h-full min-w-0 w-full flex-col rounded-xl border border-black/10 bg-white p-4">
                  <div className={`mb-2 uppercase text-black/45 ${isNative ? 'min-h-[2.4rem] text-[10px] leading-tight tracking-wide' : 'text-xs tracking-[0.2em]'}`}>
                    Tổng số phòng
                  </div>
                  <div className="mt-auto text-2xl font-semibold text-black sm:text-3xl">
                    {propertyCounts.total.toLocaleString('vi-VN')}
                  </div>
                </div>

                <div className="flex h-full min-w-0 w-full flex-col rounded-xl border border-black/10 bg-white p-4">
                  <div className={`mb-2 uppercase text-black/45 ${isNative ? 'min-h-[2.4rem] text-[10px] leading-tight tracking-wide' : 'text-xs tracking-[0.2em]'}`}>
                    Đã cho thuê
                  </div>
                  <div className="mt-auto text-2xl font-semibold text-black sm:text-3xl">
                    {propertyCounts.rented.toLocaleString('vi-VN')}
                  </div>
                </div>
              </div>

              <div className={`w-full self-start rounded-xl border border-black/10 bg-white p-4 ${isNative ? 'col-span-2' : ''}`}>
                <div className="text-xs uppercase tracking-[0.2em] text-black/45 mb-2">Đang chờ thuê</div>
                <div className="text-2xl sm:text-3xl font-semibold text-black">
                  {propertyCounts.pending.toLocaleString('vi-VN')}
                </div>
                {pendingRoomItems.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {pendingRoomItems.map((room) => (
                      <div
                        key={room.id}
                        className="rounded-lg border border-black/10 bg-[#f7f4eb]/70 px-3 py-2"
                      >
                        <div className="text-xs font-medium text-black leading-snug">
                          {room.address || 'Chưa có địa chỉ'}
                        </div>
                        <div className="mt-0.5 text-[11px] text-black/65">
                          Số phòng: {room.roomNumber || '—'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={`w-full self-start rounded-xl border border-black/10 bg-white p-4 ${isNative ? 'col-span-2' : ''}`}>
                <div className="text-xs uppercase tracking-[0.2em] text-black/45 mb-2">
                  Tiền nhà đã thu — {currentMonthLabel}
                </div>
                <div className="text-2xl sm:text-3xl font-semibold text-black">
                  {formatCurrency(
                    stats?.contract_analytics?.total_received_current_month ??
                    currentMonthRentReceived ??
                    0
                  )}
                </div>
                {!isNative && (
                  <p className="mt-2 text-xs text-black/50">
                    Tổng các khoản đã thanh toán cho kỳ thu {currentMonthLabel.toLowerCase()}
                  </p>
                )}
              </div>

              <div className={`w-full self-start rounded-xl border border-black/10 bg-white p-4 ${isNative ? 'col-span-2' : ''}`}>
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-red-600">
                  Phòng chưa thanh toán
                </div>
                <div className="text-2xl sm:text-3xl font-semibold text-black">
                  {propertyCounts.unpaidRooms.toLocaleString('vi-VN')}
                </div>

                {unpaidRoomGroups.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {unpaidRoomGroups.map((room) => (
                      <div
                        key={room.propertyId}
                        className="rounded-lg border border-black/10 bg-[#f7f4eb]/70 px-3 py-2"
                      >
                        <div className="text-xs font-medium text-black leading-snug">
                          {room.address || 'Chưa có địa chỉ'}
                        </div>
                        <div className="mt-0.5 text-[11px] text-black/65">
                          Số phòng: {room.roomNumber || '—'}
                        </div>
                        {room.periods.length > 0 ? (
                          <ul className="mt-1.5 space-y-1">
                            {room.periods.map((period) => (
                              <li
                                key={`${room.propertyId}-${period.key}`}
                                className="rounded-md bg-white/80 px-2 py-1 text-[11px] text-red-700"
                              >
                                Kỳ hạn: {period.term}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <div className="mt-1.5 text-[11px] text-black/45">Chưa có kỳ hạn nợ</div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className={`w-full self-start rounded-xl border border-black/10 bg-white p-4 ${isNative ? 'col-span-2' : ''}`}>
                <div className="text-xs uppercase tracking-[0.2em] text-black/45 mb-2">
                  Tổng số tiền đã thu năm {new Date().getFullYear()}
                </div>
                <div className="text-2xl sm:text-3xl font-semibold text-black">
                  {formatCurrency(stats?.contract_analytics?.total_received_current_year ?? 0)}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 lg:gap-6 items-stretch">
              <div className={`rounded-2xl border border-black/10 bg-white p-4 sm:p-6 flex flex-col ${isNative ? 'min-h-0' : 'min-h-[320px] sm:min-h-[380px]'}`}>
                <div className="mb-3 sm:mb-4 shrink-0">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-black/45 mb-2">Biểu đồ</p>
                  <h3 className="text-lg sm:text-xl font-semibold text-black">Tỷ lệ phòng</h3>
                </div>

                <div className="flex flex-1 flex-col items-center justify-center gap-4 sm:gap-6 py-2 min-h-0">
                  <div className="flex w-full flex-1 items-center justify-center min-h-[180px] sm:min-h-[220px]">
                    <div
                      className={`relative aspect-square w-full rounded-full ${isNative ? 'max-w-[9rem]' : 'max-w-[min(100%,12rem)] sm:max-w-[min(100%,16rem)] lg:max-w-[min(100%,18rem)]'}`}
                      style={{
                        background: `conic-gradient(#C4A35A 0 ${(propertyCounts.total > 0 ? (propertyCounts.rented / propertyCounts.total) * 100 : 0)}%, #E5E7EB ${(propertyCounts.total > 0 ? (propertyCounts.rented / propertyCounts.total) * 100 : 0)}% 100%)`
                      }}
                    >
                      <div className="absolute inset-[12%] rounded-full bg-white"></div>
                      <div className="absolute inset-0 flex flex-col items-center justify-center px-3 text-center">
                        <div className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-black">{propertyCounts.total}</div>
                        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-black/45">phòng</div>
                        {propertyCounts.total > 0 && (
                          <div className="mt-1 text-xs sm:text-sm text-[#C4A35A] font-medium">
                            {Math.round((propertyCounts.rented / propertyCounts.total) * 100)}% đã thuê
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="w-full max-w-sm space-y-2 sm:space-y-3 text-sm shrink-0">
                    <div className="flex items-center justify-between text-black/70">
                      <span className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#C4A35A]"></span>
                        Đã cho thuê
                      </span>
                      <span className="font-semibold text-black">{propertyCounts.rented}</span>
                    </div>
                    <div className="flex items-center justify-between text-black/70">
                      <span className="flex items-center gap-2">
                        <span className="h-3 w-3 rounded-full bg-[#E5E7EB]"></span>
                        Đang chờ thuê
                      </span>
                      <span className="font-semibold text-black">{propertyCounts.pending}</span>
                    </div>
                  </div>
                </div>
              </div>

              {!isNative && (
              <div className="rounded-2xl border border-black/10 bg-white p-4 sm:p-6 flex flex-col min-h-[320px] sm:min-h-[380px]">
                <div className="mb-3 sm:mb-4 shrink-0">
                  <p className="text-[11px] uppercase tracking-[0.3em] text-black/45 mb-2">Báo cáo</p>
                  <h3 className="text-lg sm:text-xl font-semibold text-black">{getChartLabel()}</h3>
                  {comparisonMetrics.change !== 0 && (
                    <p className={`mt-1 text-sm ${comparisonMetrics.change >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                      {comparisonMetrics.change >= 0 ? '+' : ''}
                      {comparisonMetrics.change.toFixed(1)}% so với kỳ trước
                    </p>
                  )}
                </div>

                <div className="relative w-full min-h-[200px] flex-1 sm:min-h-[260px] lg:min-h-[300px]">
                  {trendChart ? (
                    <div className="absolute inset-0">
                      {trendChart}
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dashed border-black/10 bg-[#f7f4eb] text-sm text-black/50">
                      Chưa có dữ liệu biểu đồ
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (isInline) {
    return modalContent;
  }

  return ReactDOM.createPortal(
    modalContent,
    document.body
  );
};

export default memo(DashboardModal); 
